import { FileImage, FileSpreadsheet, FileText, File } from "lucide-react";

/**
 * Pass in the total cost in pennies and it will format with commas and a dollar sign
 * @param {number} - integer
 * @returns {string}
 */
export const PrettyCost = (cost: number): string => {
    const total: string = (parseInt(cost.toString()) / 100).toFixed(2);
    return "$" + FormatMoneyToCommaString(total);
};

/**
 * FormatMoneyToCommaString function takes a numeric string as input and returns
 * the string with commas added as separators.
 *
 * @param {string|number} value - The numeric string to be formatted
 * @returns {string} - The formatted string with commas as thousands separator
 */
export const FormatMoneyToCommaString = (value: string | number): string => {
    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

/**
 * Removes any non-digit characters from a money string and returns the result.
 *
 * @param {string|number} value - The string or number representing the money value.
 * @returns {string} - The money value with only digit characters.
 */
export const FormatMoneyStringToNumeric = (value: string | number): string => {
    if (value === "") {
        value = 0;
    }
    return value.toString().replace(/[^0-9]/g, "");
};

/**
 * Converts a number of bytes into a human-readable format (e.g., KB, MB, GB).
 * @param {number} bytes - The number of bytes to convert.
 * @returns {string} - The formatted string representing the size in a human-readable format.
 */
export const bytesToPretty = (bytes: number) => {
    try {
        const units = ["B", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
        let l = 0;
        let n = bytes;
        while (n >= 1024 && ++l) {
            n /= 1024;
        }
        return `${n.toFixed(n < 10 && l > 0 ? 1 : 0)} ${units[l]}`;
    } catch (err) {
        return bytes;
    }
};

/**
 * Converts a file's MIME type to an appropriate icon.
 * Centralizes the logic for determining which icon to use based on the MIME type.
 * @param {string} mimeType - The MIME type of the file.
 * @returns {JSX.Element} - The icon representing the file type.
 */
export const mimeTypeToIcon = (mimeType: string, { className }: { className?: string } = {}) => {
    if (mimeType.startsWith("image")) return <FileImage className={`stroke-bronze-600 h-4 w-4 ${className}`} />;
    if (mimeType.startsWith("application/pdf")) return <FileText className={`stroke-bronze-600 h-4 w-4 ${className}`} />;
    if (mimeType.includes("spreadsheetml") || mimeType.includes("excel") || mimeType.includes("csv"))
        return <FileSpreadsheet className={`stroke-bronze-600 h-4 w-4 ${className}`} />;
    return <File className={`stroke-bronze-600 h-4 w-4 ${className}`} />;
};

export const mimeTypeToString = (mimeType: string) => {
    if (mimeType.startsWith("image")) return "Image";
    if (mimeType.startsWith("application/pdf")) return "PDF Document";
    if (mimeType.includes("spreadsheetml") || mimeType.includes("excel") || mimeType.includes("csv")) return "Spreadsheet";
    return "File";
};

export function userDisplayName(user?: any): string {
    let formatted = "";

    if (!user) return formatted;

    // Fallback to email when user's first/last name aren't included
    if (!user.first_name && !user.last_name && user.email) {
        formatted += user.email;
    }

    if (user.first_name) {
        formatted += user.first_name;
    }

    if (user.first_name && user.last_name) {
        formatted += " " + user.last_name;
    }

    return formatted;
}

export function rfqUpdatedAtShortDate(dateString: string) {
    const datePart = new Date(dateString).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
    });

    const timePart = new Date(dateString).toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
    });

    return `${datePart} ${timePart}`;
}

/**
 * What a table cell shows for a value that may be missing.
 */
export function cellText(value: string | number | null | undefined): string {
    return value === null || value === undefined || String(value).trim() === "" ? "—" : String(value);
}

export function filterChipDate(date: Date): string {
    return date.toLocaleDateString("en-US", { month: "numeric", day: "numeric", year: "2-digit" });
}

export function Capitalized(txt: string) {
    return txt.charAt(0).toUpperCase() + txt.slice(1).toLowerCase();
}

export function customDateRangeFormat(date: Date): string {
    return date
        .toLocaleDateString("en-US", {
            month: "2-digit",
            day: "2-digit",
            year: "numeric",
        })
        .replace(/\//g, " / ");
}

// Utility to truncate the middle of a filename, preserving extension, a few leading, and a few trailing characters before the extension
export function truncateFilename(filename: string, maxLength = 40): string {
    // If the filename is already short enough, return it as is
    if (filename.length <= maxLength) {
        return filename;
    }

    // Find the extension
    const extIndex = filename.lastIndexOf(".");
    const ext = extIndex !== -1 ? filename.slice(extIndex) : "";
    const base = extIndex !== -1 ? filename.slice(0, extIndex) : filename;

    // How many chars to keep at start and before extension
    const keepStart = 24;
    const keepEnd = 6; // chars before extension
    const ellipsis = "...";

    // If base is too short, fallback to just truncating the end
    if (base.length <= keepStart + keepEnd) {
        return filename.slice(0, maxLength - ellipsis.length) + ellipsis;
    }

    const start = base.slice(0, keepStart);
    const end = base.slice(-keepEnd);

    // Compose the truncated filename
    return `${start}${ellipsis}${end}${ext}`;
}

// Utility to format hours into a more human-readable string, e.g. "3 hours", "<1 hour", "1 hour", "--" for null
export const formatHours = (hours: number | null): string => {
    if (hours === null) {
        return "--";
    }
    if (hours < 1) {
        return "<1 hour";
    }
    if (hours === 1) {
        return "1 hour";
    }
    if (hours >= 24) {
        const days = hours / 24;
        if (days < 2) {
            return "1 day";
        }
        return `${Math.round(days)} days`;
    }
    return `${Math.round(hours)} hours`;
};

/**
 * Format a duration as "5d 13h 25m" — the precision buyers asked for on cycle time,
 * where "5 days" hides whether a queue is drifting by hours.
 *
 * Units below the largest are always shown so the width stays stable down a column;
 * a sub-minute duration reads "0m" rather than rounding away to nothing.
 */
export const formatDurationDHM = (milliseconds: number | null): string => {
    if (milliseconds === null || !Number.isFinite(milliseconds)) {
        return "--";
    }

    const totalMinutes = Math.floor(Math.max(0, milliseconds) / 60_000);
    const days = Math.floor(totalMinutes / 1_440);
    const hours = Math.floor((totalMinutes % 1_440) / 60);
    const minutes = totalMinutes % 60;

    if (days > 0) return `${days}d ${hours}h ${minutes}m`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
};
